import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { getSupabaseClient } from '../../lib/supabase';

const ANNOTATION = 'consiliency.io/prompt-set-ids';

const PORTAL_URL =
  process.env.PORTAL_URL ?? 'https://portal.consiliency.io';

type LifecycleState = 'draft' | 'test' | 'approved' | 'production' | 'retired';
type ApprovalState = 'none' | 'requested' | 'approved' | 'rejected' | 'withdrawn';
type RegressionSeverity = 'minor' | 'major' | 'blocker';

interface PromptSetRow {
  id: string;
  name: string;
  productionVersion: string | null;
  lifecycleState: LifecycleState | null;
  approvalState: ApprovalState | null;
  regressionCount: number;
  maxSeverity: RegressionSeverity | null;
}

const useStyles = makeStyles(theme => ({
  blocker: {
    animation: '$pulse 1.2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
}));

function lifecycleChipProps(state: LifecycleState | null): {
  label: string;
  color: 'default' | 'primary' | 'secondary';
  variant: 'default' | 'outlined';
  style?: React.CSSProperties;
} {
  switch (state) {
    case 'draft':
      return { label: 'draft', color: 'default', variant: 'outlined' };
    case 'test':
      return { label: 'test', color: 'default', variant: 'default', style: { backgroundColor: '#e3f2fd', color: '#1565c0' } };
    case 'approved':
      return { label: 'approved', color: 'default', variant: 'default', style: { backgroundColor: '#e8f5e9', color: '#2e7d32' } };
    case 'production':
      return { label: 'production', color: 'primary', variant: 'default' };
    case 'retired':
      return { label: 'retired', color: 'default', variant: 'outlined', style: { color: '#9e9e9e', borderColor: '#9e9e9e' } };
    default:
      return { label: 'unknown', color: 'default', variant: 'outlined' };
  }
}

function approvalChipProps(state: ApprovalState | null): {
  label: string;
  color: 'default' | 'primary' | 'secondary';
  variant: 'default' | 'outlined';
  style?: React.CSSProperties;
} | null {
  switch (state) {
    case null:
    case 'none':
    case 'withdrawn':
      return null;
    case 'requested':
      return { label: 'approval requested', color: 'default', variant: 'default', style: { backgroundColor: '#fff8e1', color: '#f57f17' } };
    case 'approved':
      return { label: 'approved', color: 'default', variant: 'default', style: { backgroundColor: '#e8f5e9', color: '#2e7d32' } };
    case 'rejected':
      return { label: 'rejected', color: 'secondary', variant: 'default' };
    default:
      return null;
  }
}

function regressionChipProps(count: number, severity: RegressionSeverity | null, classes: ReturnType<typeof useStyles>): {
  label: string;
  color: 'default' | 'primary' | 'secondary';
  variant: 'default' | 'outlined';
  style?: React.CSSProperties;
  className?: string;
} | null {
  if (count === 0) return null;
  const label = `${count} regression${count === 1 ? '' : 's'}`;
  switch (severity) {
    case 'minor':
      return { label, color: 'default', variant: 'default', style: { backgroundColor: '#fff8e1', color: '#f57f17' } };
    case 'major':
      return { label, color: 'secondary', variant: 'default' };
    case 'blocker':
      return { label, color: 'secondary', variant: 'default', className: classes.blocker };
    default:
      return null;
  }
}

async function fetchPromptSets(ids: string[]): Promise<PromptSetRow[]> {
  const db = getSupabaseClient();

  const { data: sets } = await db
    .from('prompt_sets')
    .select('id, name')
    .in('id', ids);

  if (!sets || sets.length === 0) return [];

  const rows: PromptSetRow[] = await Promise.all(
    sets.map(async set => {
      const { data: versions } = await db
        .from('prompt_versions')
        .select('id, lifecycle_state, approval_state, is_active_production')
        .eq('prompt_set_id', set.id)
        .order('created_at', { ascending: false })
        .limit(20);

      const prodVersion = versions?.find(v => v.is_active_production) ?? null;
      const latestVersion = versions?.[0] ?? null;
      const activeVersion = prodVersion ?? latestVersion;

      let regressionCount = 0;
      let maxSeverity: RegressionSeverity | null = null;

      if (activeVersion) {
        const { data: regressions } = await db
          .from('prompt_eval_regressions')
          .select('severity')
          .eq('prompt_version_id', activeVersion.id)
          .is('acknowledged_at', null);

        if (regressions && regressions.length > 0) {
          regressionCount = regressions.length;
          const severityOrder: RegressionSeverity[] = ['blocker', 'major', 'minor'];
          for (const s of severityOrder) {
            if (regressions.some(r => r.severity === s)) {
              maxSeverity = s;
              break;
            }
          }
        }
      }

      return {
        id: set.id,
        name: set.name,
        productionVersion: prodVersion ? prodVersion.id : null,
        lifecycleState: activeVersion?.lifecycle_state ?? null,
        approvalState: activeVersion?.approval_state ?? null,
        regressionCount,
        maxSeverity,
      };
    }),
  );

  return rows;
}

export function PromptLifecycleCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const annotation: string | undefined =
    entity.metadata.annotations?.[ANNOTATION];

  const ids = annotation
    ? annotation
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

  const [rows, setRows] = useState<PromptSetRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ids.length === 0) return;
    setLoading(true);
    fetchPromptSets(ids)
      .then(setRows)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotation]);

  if (ids.length === 0) return null;

  return (
    <Card>
      <CardHeader title="Prompt Lifecycle" />
      <Divider />
      <CardContent>
        {loading ? (
          <Typography variant="body2" color="textSecondary">
            Loading…
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No prompt sets found.
          </Typography>
        ) : (
          <List dense disablePadding>
            {rows.map(row => {
              const lcChip = lifecycleChipProps(row.lifecycleState);
              const appChip = approvalChipProps(row.approvalState);
              const regChip = regressionChipProps(row.regressionCount, row.maxSeverity, classes);

              return (
                <ListItem key={row.id} disableGutters alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Link
                        href={`${PORTAL_URL}/prompt-studio/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                      >
                        {row.name}
                      </Link>
                    }
                    secondary={
                      <span className={classes.row}>
                        {row.productionVersion ? (
                          <Chip
                            size="small"
                            label={`v${row.productionVersion.slice(0, 8)}`}
                            color="default"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="no production version"
                            color="default"
                            variant="outlined"
                          />
                        )}
                        <Chip size="small" {...lcChip} />
                        {appChip && <Chip size="small" {...appChip} />}
                        {regChip && <Chip size="small" {...regChip} />}
                      </span>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
